// Right to erasure in the Session Inspector: find the sessions of a signed-in
// person by name or user hash, and delete one session at a time. The backend
// (`api/admin_delete.py`) hashes a name and never stores it; a delete removes
// the session with its turns and events and keeps a deletion record.

import { useEffect, useRef, useState } from "preact/hooks";

import { api } from "@/api/client";
import type { DeleteSessionResponse, UserSessionsBody, UserSessionsResponse } from "@/api/types";
import { t } from "@/i18n";
import { formatDateTime, formatInt, modeLabel } from "@/lib/format";
import { errorDetail, MAX_REASON, reasonValid } from "@/lib/runtimeChange";

// A user hash as the backend's compute_pseudonym writes it.
const USER_HASH = /^[0-9a-f]{64}$/;

/** What the lookup sends: a user hash (any case) or a name; null for an empty field. */
export function lookupBody(input: string): UserSessionsBody | null {
  const text = input.trim();
  if (!text) return null;
  const lower = text.toLowerCase();
  return USER_HASH.test(lower) ? { pseudonym: lower } : { name: text };
}

/** What the operator types to confirm a delete: the start of the session id. */
export function confirmationText(sessionId: string): string {
  return sessionId.slice(0, 8);
}

/** A finished delete in words. */
export function deletionSummary(result: DeleteSessionResponse): string {
  const id = confirmationText(result.session_id);
  if (!result.session_existed && result.turns_deleted === 0 && result.events_deleted === 0) {
    return t("sessions.erasure.nothing", { id });
  }
  return t("sessions.erasure.done", {
    id,
    turns: formatInt(result.turns_deleted),
    events: formatInt(result.events_deleted),
  });
}

// A refused delete names where it stopped: at the session document (nothing
// removed) or in the purge after it (the document gone, the counts say how
// far the rest got).
function deletionError(err: unknown): string {
  const detail = errorDetail(err);
  if (detail.error === "session_delete_failed") {
    if (detail.stage === "document") return t("sessions.erasure.failed_document");
    return t("sessions.erasure.failed_purge", {
      turns: formatInt(Number(detail.turns_deleted ?? 0)),
      events: formatInt(Number(detail.events_deleted ?? 0)),
    });
  }
  return err instanceof Error ? err.message : String(err);
}

export function UserLookup({
  seed,
  refresh = 0,
  onOpen,
}: {
  // A hash handed over from the open session; looked up when it changes.
  seed: string;
  // Bumped after a delete: the last lookup runs again.
  refresh?: number;
  onOpen: (sessionId: string) => void;
}) {
  const [text, setText] = useState("");
  const [result, setResult] = useState<UserSessionsResponse | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const last = useRef<UserSessionsBody | null>(null);

  const run = async (body: UserSessionsBody | null) => {
    if (!body) return;
    last.current = body;
    setBusy(true);
    setError("");
    try {
      setResult(await api.userSessions(body));
    } catch (err) {
      setResult(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (seed) {
      setText(seed);
      void run(lookupBody(seed));
    }
  }, [seed]);

  useEffect(() => {
    if (refresh > 0) void run(last.current);
  }, [refresh]);

  return (
    <div class="card">
      <h2>{t("sessions.erasure.lookupTitle")}</h2>
      <p class="muted">{t("sessions.erasure.lookupHelp")}</p>
      <div style="display: flex; gap: 8px;">
        <input
          type="text"
          value={text}
          aria-label={t("sessions.erasure.lookupLabel")}
          placeholder={t("sessions.erasure.lookupPlaceholder")}
          onInput={(e) => setText((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void run(lookupBody(text));
          }}
          style="flex: 1; padding: 8px 12px; border: 1px solid var(--color-card-border); border-radius: var(--radius-s); font-size: 13px;"
        />
        <button
          type="button"
          class="window-pill"
          disabled={busy || lookupBody(text) === null}
          onClick={() => void run(lookupBody(text))}
        >
          {t("sessions.erasure.lookupRun")}
        </button>
      </div>
      {error && (
        <div class="error-banner" role="alert" style="margin-top: 12px;">
          {error}
        </div>
      )}
      {result && result.sessions.length === 0 && (
        <p class="muted">{t("sessions.erasure.none")}</p>
      )}
      {result && result.sessions.length > 0 && (
        <>
          <p class="muted" style="font-size: 12px; word-break: break-all;">
            {t("sessions.erasure.hash", { hash: result.pseudonym })}
          </p>
          <table class="table">
            <thead>
              <tr>
                <th>{t("sessions.col.when")}</th>
                <th>{t("sessions.col.mode")}</th>
                <th>{t("sessions.meta.turns")}</th>
                <th>{t("sessions.erasure.col.role")}</th>
                <th>{t("sessions.erasure.col.preview")}</th>
              </tr>
            </thead>
            <tbody>
              {result.sessions.map((s) => (
                <tr key={s.session_id} onClick={() => onOpen(s.session_id)} style="cursor: pointer;">
                  <td class="muted">{formatDateTime(s.created_at)}</td>
                  <td>{modeLabel(s.chat_mode)}</td>
                  <td>{formatInt(s.turn_count)}</td>
                  <td>{s.user_role || "—"}</td>
                  <td>{s.first_message_preview}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export function DeleteSessionCard({
  sessionId,
  onDeleted,
}: {
  sessionId: string;
  onDeleted: (result: DeleteSessionResponse) => void;
}) {
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // State updates render later; a second click before that must send nothing.
  const pending = useRef(false);

  useEffect(() => {
    setReason("");
    setConfirm("");
    setError("");
  }, [sessionId]);

  const expected = confirmationText(sessionId);
  const ready = reasonValid(reason) && confirm.trim() === expected && !busy;

  const submit = async () => {
    if (!ready || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      onDeleted(await api.deleteSession(sessionId, reason.trim()));
    } catch (err) {
      setError(deletionError(err));
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  return (
    <section class="card ops-danger">
      <h2>{t("sessions.erasure.deleteTitle")}</h2>
      <p class="muted">{t("sessions.erasure.deleteHelp")}</p>
      <div style="display: grid; gap: 8px;">
        <input
          class="ops-reason"
          type="text"
          maxLength={MAX_REASON}
          value={reason}
          aria-label={t("sessions.erasure.reason")}
          placeholder={t("sessions.erasure.reason")}
          onInput={(e) => setReason((e.target as HTMLInputElement).value)}
        />
        {!reasonValid(reason) && <div class="kpi-sub">{t("ops.reason.missing")}</div>}
        <input
          class="ops-reason"
          type="text"
          value={confirm}
          aria-label={t("sessions.erasure.confirm", { text: expected })}
          placeholder={t("sessions.erasure.confirm", { text: expected })}
          onInput={(e) => setConfirm((e.target as HTMLInputElement).value)}
        />
        <div>
          <button
            type="button"
            class="ops-danger-button"
            disabled={!ready}
            onClick={() => void submit()}
          >
            {t("sessions.erasure.delete")}
          </button>
        </div>
      </div>
      {error && (
        <div class="error-banner" role="alert" style="margin-top: 12px;">
          {error}
        </div>
      )}
    </section>
  );
}
